import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Avatar,
  Skeleton,
  Card,
  CardContent,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  CircularProgress,
  TextField,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { getWebAppUrl } from '../../config/runtimeConfig';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { getIssuerBadgeProfile, updateProfileId, getUserContext } from '../../services/apiservice';
import { useAuth } from '../../contexts/authContext';
import PeopleIcon from '@mui/icons-material/People';
import SnackbarComponent from '../../utils/SnackbarComponent';
import { Helmet } from 'react-helmet-async';
import { Title } from '@mui/icons-material';
import { getSelectedRole } from '../../utils/auth';
import { useApiData } from '../../hooks/useApi';

const IssuerBadgesProfile = ({ issuerID }) => {
  const { user, setUser } = useAuth();
  const ProfileID = user.ProfileID;
  const [sortOrder, setSortOrder] = useState('Most Recent');
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [issuerData, setIssuerData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { data: profileResp, loading: isProfileLoading, error: profileError } = useApiData(() => getIssuerBadgeProfile(ProfileID), [ProfileID, user, setUser]);
  const [newProfileID, setNewProfileID] = useState(ProfileID);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  // const email = 
  // user.role === "User" && user.IssuerRepEmailID 
  //   ? user.IssuerRepEmailID 
  //   : user.UserEmail;
  const selectedRole = getSelectedRole()?.selectedRole || null;
  const allowedRoles = ['Admin', 'User'];
  const email = allowedRoles.includes(user.role) ? user.UserEmail : user.IssuerRepEmailID;
  const profileLink = `${getWebAppUrl()}/issuers/${ProfileID}`;

  useEffect(() => {
    if (profileError) {
      setSnackbarMessage('An error occurred while fetching issuer data.');
      setSnackbarOpen(true);
    } else if (profileResp) {
      if (profileResp.success) {
        setIssuerData(profileResp.data);
      } else {
        setSnackbarMessage('Failed to fetch issuer data.');
        setSnackbarOpen(true);
      }
    }
  }, [profileResp, profileError]);

  const handlePublishOnLinkedIn = () => {
    const linkedInShareURL = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(profileLink)}`;
    window.open(linkedInShareURL, '_blank');
  };

  const handleProfileIDUpdate = async () => {
    if (!newProfileID.trim()) {
      setSnackbarMessage('ProfileID cannot be empty. Please enter a valid ProfileID.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      setIsEditing(true);
      return;
    }

    if (newProfileID.trim() === ProfileID) {
      setSnackbarMessage('No changes detected. Please update the ProfileID before saving.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      setIsEditing(false);
      return;
    }

    if (newProfileID.trim()) {
      setIsLoading(true);
      try {
        const response = await updateProfileId(ProfileID, newProfileID);
        if (response.success) {
          setIssuerData((prevProfile) => ({
            ...prevProfile,
            ProfileID: newProfileID,
          }));
          setNewProfileID(newProfileID);
          const userContextResponse = await getUserContext(email, selectedRole);
          if (userContextResponse.success) {
            setUser({ ...userContextResponse.data.userContext, role: selectedRole });
            setSnackbarMessage('ProfileID Updated successfully!');
            setSnackbarSeverity('success');
          } else {
            setSnackbarSeverity('error');
            setSnackbarMessage('Error fetching updated user context');
          }
          setIsEditing(false);
        } else {
          console.error('Failed to update ProfileID');
          setSnackbarMessage('Failed to update ProfileID');
          setSnackbarSeverity('error');
        }
        setSnackbarOpen(true);
      } catch (error) {
        console.error('Error updating ProfileID:', error);
        const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const sortedBadges = Array.isArray(issuerData?.Badges) ?
    [...issuerData.Badges].sort((a, b) => {
      if (sortOrder === 'Most Recent') {
        const latestAssignmentA = a.Assignments.length > 0 ? new Date(Math.max(...a.Assignments.map(assignment => new Date(assignment.AssignmentDate)))) : 0;
        const latestAssignmentB = b.Assignments.length > 0 ? new Date(Math.max(...b.Assignments.map(assignment => new Date(assignment.AssignmentDate)))) : 0;
        return latestAssignmentB - latestAssignmentA;
      }
      return b.PopularityMetric - a.PopularityMetric;
    }) : [];


  const handleCopyLink = () => {
    const link = `${getWebAppUrl()}/issuers/${ProfileID}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Container>
      <Helmet>
        <link rel="canonical" href={profileLink} />
        <title>Badge Profile</title>
      </Helmet>
      {isProfileLoading ? (
        <Box
          textAlign="center"
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Skeleton variant="circular" width={120} height={120} />
          <Skeleton variant="text" height={60} width="30%" />
          <Skeleton variant="text" height={30} width="30%" />

          <Box sx={{ marginTop: 2, marginLeft: 5, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Skeleton variant="rectangular" width={500} height={40} />
            <Box sx={{ marginLeft: '10px', height: '40px', display: 'flex', alignItems: 'center' }}>
              <Skeleton variant="rectangular" width={100} height={40} />
            </Box>
            <Box sx={{ marginLeft: '10px', height: '40px', display: 'flex', alignItems: 'center' }}>
              <Skeleton variant="rectangular" width={100} height={40} />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
            <Skeleton variant="rectangular" width={280} height={40} />
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            width="100%"
            mt={2}
            mb={3}
          >
            <Skeleton variant="text" height={40} width="15%" />
            <Skeleton variant="rectangular" width={120} height={40} />
          </Box>

          <Grid container spacing={2} justifyContent="center">
            {[...Array(6)].map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ padding: 2, textAlign: 'center' }}>
                  <Skeleton
                    variant="circular"
                    width={60}
                    height={60}
                    sx={{ margin: '0 auto' }}
                  />
                  <Skeleton variant="text" height={30} width="80%" sx={{ mt: 1, mx: 'auto' }} />
                  <Skeleton variant="text" height={20} width="60%" sx={{ mb: 1, mx: 'auto' }} />
                  <Skeleton variant="text" height={20} width="60%" sx={{ mb: 1, mx: 'auto' }} />
                  <Skeleton
                    variant="rectangular"
                    height={20}
                    width="40%"
                    sx={{ margin: '0 auto' }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : issuerData ? (

        <>
          <Box
            textAlign="center"
            marginBottom={4}
            sx={{
              padding: '16px',
              borderRadius: '8px',
              background: 'linear-gradient(to right, rgba(0, 0, 0, 0.8), rgba(169, 169, 169, 0.6), rgba(0, 0, 0, 0.8))', // Gradient from black to gray
              boxShadow: 3
            }}
          >
            <Avatar
              sx={{
                width: 120,
                height: 120,
                margin: '0 auto',
                border: '4px solid rgba(255, 255, 255, 0.8)',
                boxShadow: 2
              }}
              src={issuerData.IssuerLogoURL}
              alt={issuerData.IssuerName}
            />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#fff' }}>
              {issuerData.IssuerName}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {issuerData.IssuerWebsite}
            </Typography>
          </Box>

          <Box sx={{ marginTop: 2, marginLeft: 5, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {isEditing ? (
              <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fff', height: '40px' }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', padding: '8px', borderRight: '1px solid #ddd', minWidth: '180px' }}>
                  {`${getWebAppUrl()}/issuers/`}
                </Typography>
                <TextField
                  value={newProfileID}
                  onChange={(e) => setNewProfileID(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: '300px', height: '40px', padding: '0', margin: 0 }}
                  onBlur={(e) => {
                    const trimmedNewProfileID = newProfileID.trim();
                    if (trimmedNewProfileID === ProfileID || !trimmedNewProfileID) {
                      setIsEditing(false);
                    }
                  }}
                  autoFocus
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: '8px', padding: '8px 12px', backgroundColor: '#fff', minWidth: '300px', height: '40px' }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', marginRight: '10px' }}>
                  {`${getWebAppUrl()}/issuers/${ProfileID}`}
                </Typography>
              </Box>
            )}

            <Box sx={{ marginLeft: '10px', height: '40px', display: 'flex', alignItems: 'center' }}>
              {!copied ? (
                <Tooltip title="Copy Profile Link" arrow>
                  <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopyLink} sx={{ whiteSpace: 'nowrap', height: '100%' }}>
                    Copy
                  </Button>
                </Tooltip>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <CheckCircleIcon sx={{ color: 'green', marginRight: '4px' }} />
                  <Typography variant="body2" sx={{ color: 'green' }}>Copied!</Typography>
                </Box>
              )}
            </Box>

            <Tooltip title="Edit ProfileURL" arrow>
              <Button
                onClick={isEditing ? handleProfileIDUpdate : () => setIsEditing(!isEditing)}
                variant="outlined"
                sx={{
                  marginLeft: '10px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                disabled={isEditing && (newProfileID.trim() === '' || newProfileID.trim() === ProfileID)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {isEditing ? (
                    isLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <SaveIcon />
                    )
                  ) : (
                    <EditIcon />
                  )}
                  <Typography variant="body2" sx={{ marginLeft: 1 }}>
                    {isEditing ? 'Save' : 'Edit'}
                  </Typography>
                </Box>
              </Button>
            </Tooltip>
          </Box>

          <Box textAlign="center" marginTop={2}>
            {issuerData.Badges.length !== 0 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handlePublishOnLinkedIn}
                startIcon={<LinkedInIcon />}
              >
                Publish Badge Profile on LinkedIn
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                onClick={handlePublishOnLinkedIn}
                startIcon={<LinkedInIcon />}
                disabled
              >
                No Badges Yet - Publish Profile on LinkedIn
              </Button>
            )}
          </Box>


          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <Typography variant="h6" sx={{ textAlign: 'left', color: '#333' }}>
              {issuerData.Badges.length} Badge{issuerData.Badges.length !== 1 ? 's' : ''} Issued
            </Typography>
            <FormControl variant="outlined" sx={{ minWidth: 120 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                label="Sort By"
              >
                <MenuItem value="Most Recent">Most Recent</MenuItem>
                <MenuItem value="Most Popular">Most Popular</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Grid container spacing={3}>
            {sortedBadges.map((badge, index) => (
              <Grid item xs={12} sm={6} md={4} key={badge.CredentialID}>
                <Card sx={{
                  padding: 2,
                  textAlign: 'center',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.2)',
                    '& .credential-name': {
                      transform: 'scale(1.1)',
                    },
                  },
                  background: 'linear-gradient(145deg, #f5f7fa, #c3cfe2)',
                  borderRadius: 2,
                  boxShadow: 2,
                }}>
                  <Avatar
                    sx={{ width: 60, height: 60, margin: '0 auto' }}
                    src={badge.CredentialImageURL}
                    alt={badge.CredentialName}
                  />
                  <CardContent>
                    <Typography
                      variant="h6"
                      component="div"
                      className="credential-name"
                      sx={{
                        transition: 'transform 0.3s ease-in-out',
                      }}
                    >
                      {badge.CredentialName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {badge.CredentialDescription}
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <PeopleIcon color="primary" /> {/* Icon for recipients */}
                      <Typography variant="body2" color="primary" fontWeight="bold" sx={{ ml: 0.5 }}>
                        Recipients: {badge.Assignments.length}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        <Typography variant="h6" color="error">
          Issuer not found.
        </Typography>
      )}

      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
    </Container>
  );
};

export default IssuerBadgesProfile;
